// @ts-nocheck
import React, { useState, ChangeEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClassificationValue } from '@fulcrumapp/fulcrum-core';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { useApp } from '../../hooks/use-app';
import flattenClassifications from './helpers/flatten-classifications';

interface Props {
  hidden?: boolean,
  classificationSetId?: string,
  value?: ClassificationValue,
  onChange?: (newVal: ClassificationValue) => void,
  allowOtherValue?: boolean,
  disabled?: boolean,
  label: string,
  required?: boolean,
  withError?: boolean,
  emptyLabel?: string
}

const OTHER_VALUE = '__OTHER__';

const ClassificationsetField: React.FC<Props> = ({
  hidden = false,
  classificationSetId,
  value = new ClassificationValue(),
  label,
  required,
  onChange = () => { },
  allowOtherValue = false,
  disabled = false,
  withError,
  emptyLabel,
}: Props) => {
  const app = useApp();
  const [isBlur, setIsBlur] = useState(false);
  const [noValueOption, setNoValueOption] = useState(true);

  const loadClassificationSet = () => app.api.classificationSets.find(classificationSetId);

  const { data } = useQuery(['classification_set', classificationSetId], () => loadClassificationSet(),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    });

  const choiceValues = value?.choice_values || [];
  const otherValues = value?.other_values || [];

  const classificationItems = data ? flattenClassifications(data.items, allowOtherValue, 'child_classifications', emptyLabel) : [];

  const [otherSelected, setOtherSelected] = useState(allowOtherValue && value.hasOtherValue);

  const selected = classificationItems.find((set) => set.value === JSON.stringify(choiceValues));

  const handleFocus = () => {
    setIsBlur(false);
  };

  const handleBlur = () => {
    setIsBlur(true);
    !selected ? setNoValueOption(true) : setNoValueOption(false);
  };

  const handleError = () => (
    required && noValueOption && (withError || isBlur)
  );

  const handleOtherValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    onChange({
      choiceValues,
      otherValues: event.target.value,
    });
  };

  const handleSelectChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    const values = [];
    if (event.target.value.length) {
      const selection = JSON.parse(event.target.value);
      values.push(...selection);
    }

    const isOtherSelectedNow = values.includes(OTHER_VALUE);

    const selectedValues = values.filter((v) => (v !== OTHER_VALUE));

    // Remove empty or falsey values from the other field
    const filteredOtherValue = isOtherSelectedNow ? otherValues.filter(Boolean)[0] : null;

    setOtherSelected(isOtherSelectedNow);
    setNoValueOption(!!selectedValues.length);
    onChange({
      choiceValues: selectedValues,
      otherValues: filteredOtherValue,
    });
  };

  return (
    <>
      {!hidden && (
        <>
          <TextField
            fullWidth
            select
            label={label}
            value={selected?.value || ''}
            onChange={handleSelectChange}
            required={required}
            disabled={disabled}
            error={handleError()}
            helperText={handleError() ? 'This field is required' : ' '}
            onBlur={handleBlur}
            onFocus={handleFocus}
          >
            {classificationItems.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
              >
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          {allowOtherValue && otherSelected && (
            <TextField
              value={otherValues[0] || ''}
              onChange={handleOtherValueChange}
              disabled={disabled}
              placeholder="Enter Other Value"
            />
          )}
        </>
      )}
    </>
  );
};

export default ClassificationsetField;
